import React, { useCallback, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { Flex, Heading, Box, Text, Switch, useToast } from '@televet/kibble-ui';
import { useUpsertOneBitwerxPaymentWritebackConfigurationMutation } from 'shared/types/graphql';
import { useBitwerxData } from 'shared/providers/IntegrationsProvider';
import usePaymentWritebackConfig from 'shared/hooks/usePaymentWritebackConfig';

const WritebackSection = (): JSX.Element => {
  const { bitwerxIntegrationId } = useBitwerxData();
  const { isWritebackManualPaymentEnabled } = usePaymentWritebackConfig();
  const toast = useToast();

  const [updateWritebackSetting, { data: writebackConfigData }] =
    useUpsertOneBitwerxPaymentWritebackConfigurationMutation();

  const manualWritebacksEnabled = useMemo(() => {
    return (
      writebackConfigData?.upsertOneBitwerxPaymentWritebackConfiguration?.writebackManualPayment ||
      isWritebackManualPaymentEnabled
    );
  }, [
    writebackConfigData?.upsertOneBitwerxPaymentWritebackConfiguration?.writebackManualPayment,
    isWritebackManualPaymentEnabled,
  ]);

  const handleWritebackToggle = useCallback(
    (isChecked: boolean) => {
      if (!bitwerxIntegrationId) return;
      try {
        updateWritebackSetting({
          variables: {
            where: {
              integrationId: bitwerxIntegrationId,
            },
            create: {
              integration: {
                connect: { id: bitwerxIntegrationId },
              },
              writebackManualPayment: isChecked,
            },
            update: {
              integration: {
                connect: { id: bitwerxIntegrationId },
              },
              writebackManualPayment: isChecked,
            },
          },
          optimisticResponse: {
            upsertOneBitwerxPaymentWritebackConfiguration: {
              id: 'temp-id',
              integrationId: bitwerxIntegrationId,
              writebackManualPayment: isChecked,
              enteredById: null,
              defaultPimsPaymentTypeId: null,
              visaPimsPaymentTypeId: null,
              mastercardPimsPaymentTypeId: null,
              amexPimsPaymentTypeId: null,
              discoverPimsPaymentTypeId: null,
            },
          },
        });

        toast({ title: 'Changes saved!', status: 'success' });
      } catch (e) {
        console.error('Error updating manual writeback setting', e);
        Sentry.captureException(e);
        toast({ title: 'Error saving changes!', description: 'Please try again.', status: 'error' });
      }
    },
    [bitwerxIntegrationId, updateWritebackSetting, toast],
  );

  return (
    <Flex direction="column" maxW={570}>
      <Heading size="sm">Writebacks</Heading>
      <Flex pt={5} pb={1} gap={3} alignItems="center">
        <Heading size="xs">Manual Amount Writebacks</Heading>
        <Box width="fit-content">
          <Switch
            size="sm"
            isChecked={manualWritebacksEnabled}
            onChange={(e): void => handleWritebackToggle(e.target.checked)}
          />
        </Box>
      </Flex>
      <Text size="sm" variant="subtle">
        Writebacks to client account balance will always be attempted for payments collected through the Payments Panel,
        Text-to-Pay, and the Terminal Payments App for manually entered amounts.
        <br />
        <br />
        When disabled, payments against invoices or with account balance selected will still attempt to write back.
      </Text>
    </Flex>
  );
};

export default WritebackSection;
